import { z } from "zod";
import axios from "axios";
import { fetchApi, isApiError } from "@/lib/api-helpers";
import { validateVgsConfig } from "@/lib/vgs-utils";
import { browserInfoSchema } from "@/lib/browser-info";

const billingAddressSchema = z.object({
  id: z.number().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  address1: z.string(),
  address2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country_code: z.string(),
});

type BillingAddress = z.infer<typeof billingAddressSchema>;

export const vaultResponseSchema = z.object({
  vault: z.object({
    vault_id: z.string(),
    environment: z.string(),
    route_id: z.string(),
    requires_3ds: z.boolean().optional().default(false),
  }),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

export type VaultResponse = z.infer<typeof vaultResponseSchema>;

export async function fetchVaultCredentials(
  cartToken: string,
): Promise<VaultResponse> {
  return fetchApi({
    endpoint: `/carts/${cartToken}/vault`,
    method: "POST",
    outputSchema: vaultResponseSchema,
  });
}

// VGS Vault utility functions (stubs - need implementation)
function sanitizeCardNumber(cardNumber: string): string {
  // Remove all non-digit characters
  return cardNumber.replace(/\D/g, "");
}

function generateVgsVaultApiUrl(vaultId: string, environment: string): string {
  const envPrefix = environment === "sandbox" ? "sandbox" : "live";
  return `https://${vaultId}.${envPrefix}.verygoodvault.com`;
}

// VGS Alias Response Schema
const vgsAliasResponseSchema = z.object({
  data: z.array(
    z.object({
      aliases: z.array(
        z.object({
          alias: z.string(),
          classifier: z.string(),
        }),
      ),
    }),
  ),
});

type VgsAliasResponse = z.infer<typeof vgsAliasResponseSchema>;

function extractCardDataFromAliasResponse(
  response: VgsAliasResponse,
  originalCardNumber: string,
): {
  panAlias: string;
  cvcAlias?: string;
  cardType: string;
  lastFour: string;
} {
  const panAlias = response.data
    .find((item) =>
      item.aliases.some((alias) => alias.classifier === "card-number"),
    )
    ?.aliases.find((alias) => alias.classifier === "card-number")?.alias;

  const cvcAlias = response.data
    .find((item) =>
      item.aliases.some((alias) => alias.classifier === "card-security-code"),
    )
    ?.aliases.find((alias) => alias.classifier === "card-security-code")?.alias;

  if (!panAlias) {
    throw new Error("Failed to extract PAN alias from response");
  }

  // Extract card type and last four from original card number
  const cleanedCardNumber = sanitizeCardNumber(originalCardNumber);
  const lastFour = cleanedCardNumber.slice(-4);

  // Simple card type detection (can be enhanced)
  const firstDigit = cleanedCardNumber[0];
  let cardType = "unknown";
  if (firstDigit === "4") cardType = "visa";
  else if (firstDigit === "5") cardType = "mastercard";
  else if (firstDigit === "3") cardType = "amex";
  else if (firstDigit === "6") cardType = "discover";

  return {
    panAlias,
    cvcAlias,
    cardType,
    lastFour,
  };
}

function handleVgsError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || "VGS API error";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Unknown VGS error";
}

export async function createVgsAliases(
  vaultCredentials: VaultResponse,
  cardNumber: string,
  cvv: string,
): Promise<VgsAliasResponse> {
  const { vault_id, environment } = vaultCredentials.vault;

  if (!validateVgsConfig(vaultCredentials.vault)) {
    throw new Error("Invalid VGS configuration");
  }

  const sanitizedCardNumber = sanitizeCardNumber(cardNumber);
  const baseUrl = generateVgsVaultApiUrl(vault_id, environment);

  const requestData = {
    data: [
      {
        value: sanitizedCardNumber,
        classifiers: ["card-number"],
        format: "UUID" as const,
        storage: "PERSISTENT" as const,
      },
      {
        value: cvv,
        classifiers: ["card-security-code"],
        format: "UUID" as const,
        storage: "VOLATILE" as const,
      },
    ],
  };

  try {
    // Note: VGS Vault API typically requires authentication via route configuration
    // rather than Bearer token. This may need adjustment based on actual VGS setup.
    const response = await axios.post(`${baseUrl}/aliases`, requestData, {
      headers: {
        "Content-Type": "application/json",
      },
      validateStatus: (status) => status === 200 || status === 201,
    });

    return vgsAliasResponseSchema.parse(response.data);
  } catch (error) {
    console.error("VGS Vault API Error:", error);
    const errorMessage = handleVgsError(error);
    throw new Error(errorMessage);
  }
}

// Schema for VGS Vault payment method with optional browser info
export const updatePaymentMethodWithVgsVaultSchema = z.object({
  billing_address: billingAddressSchema,
  payment_method: z.object({
    token_provider: z.literal("vgs"),
    token: z.string(),
    cvv_token: z.string().optional(),
    last_four: z.string().optional(),
    card_type: z.string().optional(),
    exp_month: z.string().optional(),
    exp_year: z.string().optional(),
    card_holder: z.string().optional(),
  }),
  browser_info: browserInfoSchema.optional(),
});

export interface VgsVaultTokenizationParams {
  cartToken: string;
  paymentAccountId: string;
  billing_address: BillingAddress;
  cardNumber: string;
  cvv: string;
}

export async function handleVgsVaultTokenization({
  cartToken,
  billing_address,
  cardNumber,
  cvv,
  additionalData,
}: VgsVaultTokenizationParams & {
  additionalData?: {
    last_four?: string;
    card_type?: string;
    exp_month?: string;
    exp_year?: string;
    card_holder?: string;
  };
}): Promise<{ panAlias: string; cvcAlias?: string }> {
  try {
    const vaultCredentials = await fetchVaultCredentials(cartToken);

    const aliasResponse = await createVgsAliases(
      vaultCredentials,
      cardNumber,
      cvv,
    );

    const { panAlias, cvcAlias, cardType, lastFour } =
      extractCardDataFromAliasResponse(aliasResponse, cardNumber);

    if (!panAlias) {
      throw new Error("Failed to extract PAN alias from VGS Vault response");
    }

    const requestBody: z.infer<typeof updatePaymentMethodWithVgsVaultSchema> = {
      billing_address,
      payment_method: {
        token_provider: "vgs",
        token: panAlias,
        last_four: lastFour,
        card_type: cardType,
        ...additionalData,
        ...(cvcAlias && { cvv_token: cvcAlias }),
      },
    };

    await fetchApi({
      endpoint: `/carts/${cartToken}/payment_methods/credit_card`,
      method: "POST",
      input: requestBody,
      inputSchema: updatePaymentMethodWithVgsVaultSchema,
      outputSchema: z.object({}),
    });

    return { panAlias, cvcAlias };
  } catch (error) {
    if (isApiError(error)) {
      throw error;
    }

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Failed to process payment method securely");
  }
}
export async function fetchVaultCredentialsForPaymentAccount(
  jwt: string,
): Promise<VaultResponse> {
  return fetchApi({
    endpoint: `/fluid_pay/vault?jwt=${jwt}`,
    method: "GET",
    outputSchema: vaultResponseSchema,
  });
}

export const updatePaymentMethodWithVgsSchema = z.object({
  billing_postal_code: z.string(),
  payment_account: z.string().optional(),
  payment_method: z.object({
    token_provider: z.literal("vgs"),
    token: z.string(),
    last_four: z.string().optional(),
    card_type: z.string().optional(),
    cvv_token: z.string().optional(),
    exp_month: z.string(),
    exp_year: z.string(),
    card_holder: z.string().optional(),
  }),
});

export const vgsCollectSubmitResponseSchema = z.object({
  card_number: z.string(),
  card_exp: z.string(),
  card_cvc: z.string(),
  card_holder: z.string(),
});

export const creditCardResponseSchema = z.object({
  card: z
    .object({
      vgs_card_id: z.string().optional(),
    })
    .optional(),
  success: z.boolean().optional(),
});

export type VgsCollectSubmitResponse = {
  card_number: string;
  card_exp: string;
  card_cvc: string;
  card_holder: string;
};

export type CreditCardResponse = z.infer<typeof creditCardResponseSchema>;

export interface VgsCollectTokenizationParams {
  cartToken: string;
  paymentAccountId?: string;
  billing_postal_code: string;
  submitResponse: VgsCollectSubmitResponse;
  card_holder?: string;
  exp_month: string;
  exp_year: string;
}

export async function handleVgsCollectTokenization({
  cartToken,
  paymentAccountId,
  billing_postal_code,
  submitResponse,
  card_holder,
  exp_month,
  exp_year,
}: VgsCollectTokenizationParams): Promise<CreditCardResponse> {
  const cardNumberToken = submitResponse.card_number;
  const cardCvcToken = submitResponse.card_cvc;

  if (!cardNumberToken) {
    throw new Error("Failed to extract card token from VGS response");
  }

  const expYearFull = exp_year.length === 2 ? `20${exp_year}` : exp_year;

  const requestBody: z.infer<typeof updatePaymentMethodWithVgsSchema> = {
    billing_postal_code,
    ...(paymentAccountId && { payment_account: paymentAccountId }),
    payment_method: {
      token_provider: "vgs",
      token: cardNumberToken,
      cvv_token: cardCvcToken,
      exp_month,
      exp_year: expYearFull,
      card_holder,
    },
  };

  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/credit_card`,
    method: "POST",
    input: requestBody,
    inputSchema: updatePaymentMethodWithVgsSchema,
    outputSchema: creditCardResponseSchema,
  });
}
